import { type FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../../../components/Button/Button'
import { Spinner } from '../../../../components/Spinner/Spinner'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { addToast } from '../../../../store/toast/toastSlice'
import {
  useDeleteSchedulingEventTypeMutation,
  useDuplicateSchedulingEventTypeMutation,
  useGetSchedulingEventTypesQuery,
  useUpsertSchedulingEventTypeMutation,
} from '../../../../store/scheduling/scheduling'
import { EventTypeAccess, type SchedulingEventType } from '../../../../models/Scheduling'
import { EventTypeList } from '../../../Scheduling/shared/EventTypeList'
import { EventTypesInfoTooltip } from '../../../Scheduling/shared/EventTypesInfoTooltip'
import { EventTypeCreateModal } from '../../../Scheduling/shared/EventTypeCreateModal'
import { ConfirmDialog } from '../../../Scheduling/shared/ConfirmDialog'
import { SettingsCard, SettingsRow, SettingsSection } from './SettingsSection'

export const SharedEventTypesSection: FC<{ readOnly: boolean }> = ({ readOnly }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const accountId = authenticatedUser.accountId
  const { data: eventTypes, isLoading, isFetching } = useGetSchedulingEventTypesQuery({ accountId })
  const [upsertEventType, { isLoading: isCreating }] = useUpsertSchedulingEventTypeMutation()
  const [duplicateEventType, { isLoading: isDuplicating }] = useDuplicateSchedulingEventTypeMutation()
  const [deleteEventType, { isLoading: isDeleting }] = useDeleteSchedulingEventTypeMutation()
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [eventTypeToDelete, setEventTypeToDelete] = useState<SchedulingEventType | null>(null)
  const isSaving = isDuplicating || (isFetching && !isLoading)

  const accountEventTypes = (eventTypes ?? []).filter((eventType) => eventType.access === EventTypeAccess.ACCOUNT)

  const handleCreate = async (eventType: SchedulingEventType) => {
    const result = await upsertEventType({ accountId, eventType })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not create the event type', messages: ['Please try again.'] }))
    } else {
      dispatch(addToast({ variant: 'success', title: 'Event type created', messages: [`${result.data.name} is ready to use.`] }))
      setIsCreateModalOpen(false)
    }
  }

  const handleDuplicate = async (eventType: SchedulingEventType) => {
    const result = await duplicateEventType({ accountId, eventTypeId: eventType.id })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not duplicate the event type', messages: ['Please try again.'] }))
    } else {
      dispatch(addToast({ variant: 'success', title: 'Event type duplicated', messages: [`${result.data.name} was created as your personal copy.`] }))
    }
  }

  const handleDelete = async () => {
    if (!eventTypeToDelete) {
      return
    }
    const result = await deleteEventType({ accountId, eventTypeId: eventTypeToDelete.id })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not delete the event type', messages: ['Please try again.'] }))
      return
    }
    dispatch(addToast({ variant: 'success', title: 'Event type deleted', messages: [`${eventTypeToDelete.name} was removed.`] }))
    setEventTypeToDelete(null)
  }

  return (
    <SettingsSection
      title='Shared event types'
      subtitle='Set up the event types your clients can book. Users in your account activate and customise these from their profile.'
      titleAdornment={<EventTypesInfoTooltip />}
      isSaving={isSaving}
      action={
        !readOnly ? (
          <Button variant='primary' className='text-nowrap' onClick={() => setIsCreateModalOpen(true)}>
            <i className='bi bi-plus-circle me-1' />
            New event type
          </Button>
        ) : null
      }
    >
      <SettingsCard>
        {isLoading ? (
          <SettingsRow className='d-flex justify-content-center'>
            <Spinner />
          </SettingsRow>
        ) : (
          <EventTypeList
            eventTypes={accountEventTypes}
            readOnly={readOnly}
            onEdit={(eventType) => navigate(`/workspace/setup/scheduling/scheduling-event-type/edit/${eventType.id}/details`)}
            onDuplicate={handleDuplicate}
            onDelete={setEventTypeToDelete}
          />
        )}
      </SettingsCard>
      {isCreateModalOpen ? (
        <EventTypeCreateModal access={EventTypeAccess.ACCOUNT} isCreating={isCreating} onCancel={() => setIsCreateModalOpen(false)} onCreate={handleCreate} />
      ) : null}
      {eventTypeToDelete ? (
        <ConfirmDialog
          title='Delete event type'
          body={`Delete "${eventTypeToDelete.name}"? Users can no longer offer it; existing bookings are not affected.`}
          confirmLabel='Delete'
          isLoading={isDeleting}
          onConfirm={handleDelete}
          onCancel={() => setEventTypeToDelete(null)}
        />
      ) : null}
    </SettingsSection>
  )
}
